import { FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../contexts/UserContext';
import { createCompanyForOwner } from '../utils/createCompanyForOwner';
import { Btn, Card, InputField } from '../components/ui';

import '../css/NewCompanyRegistration.css';

/**
 * Slim post-signup step: CompanyOwner exists (e.g. free-demo) but no Company yet.
 * Creates Company, links CompanyOwner.companyId, refreshes context, goes home.
 */
export const NewCompanyRegistration = () => {
  const navigate = useNavigate();
  const { companyOwner, company, businessAdmin, getUserDetails, isLoading } = useUserContext();
  const [companyName, setCompanyName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="ncr-page">
        <p className="ncr-muted">Loading…</p>
      </div>
    );
  }

  if (businessAdmin) {
    return <Navigate to="/" replace />;
  }

  if (!companyOwner) {
    return <Navigate to="/" replace />;
  }

  if (company) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = companyName.trim();
    if (!trimmed) {
      setError('Enter a company name to continue.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await createCompanyForOwner({ companyOwner, name: trimmed });
      await getUserDetails();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Failed to create company:', err);
      Sentry.captureException(err);
      setError('Could not create your company. Please try again.');
      setIsSaving(false);
    }
  };

  return (
    <div className="ncr-page">
      <Card variant="card-lg" className="ncr-card card-pad-lg">
        <h1 className="ncr-title">Register your company</h1>
        <p className="ncr-lede">
          You&apos;re signed in as the account owner. Add a company name to finish setup — you can
          edit details later.
        </p>
        <form className="ncr-form" onSubmit={handleSubmit}>
          <InputField
            label="Company name"
            name="companyName"
            value={companyName}
            onChange={(value) => {
              setCompanyName(value);
              if (error) setError(null);
            }}
            placeholder="Your Company Name"
            disabled={isSaving}
            hasError={Boolean(error)}
            ariaDescribedBy={error ? 'ncr-error' : undefined}
            autoComplete="organization"
          />
          {error ? (
            <p id="ncr-error" className="ncr-error" role="alert">
              {error}
            </p>
          ) : null}
          <Btn
            variant="primary"
            buttonText={isSaving ? 'Creating…' : 'Continue'}
            buttonType="submit"
            buttonDisabled={isSaving || !companyName.trim()}
          />
        </form>
      </Card>
    </div>
  );
};
